"use client";

import { useEffect, useRef } from "react";
import { gsap } from "gsap";

/* Hero entrance, timed like atlassian.com: each [data-hero-reveal] block fades
   up 20px over 400ms on an ease-out-cubic curve (cubic-bezier(.33,1,.68,1) ===
   GSAP power2.out), 50ms apart, in the order of the attribute value.
   hero.css hides those blocks only when scripting is on and motion is allowed,
   so no-JS and reduced-motion visitors always see the copy immediately.

   The headline emphasis carries a floating green gradient: three soft colour
   fields (the original title-accent greens) drift inside the letters. GSAP
   moves their centres (CSS custom properties) to new random points forever,
   and pauses while the hero is off screen. */
const BLOBS = ["1", "2", "3"] as const;

export function HeroReveal() {
  const marker = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const scope = marker.current?.closest("section");
    if (!scope) return;
    const reduce = matchMedia("(prefers-reduced-motion: reduce)").matches;
    const items = Array.from(
      scope.querySelectorAll<HTMLElement>("[data-hero-reveal]"),
    ).sort(
      (a, b) =>
        Number(a.dataset.heroReveal) - Number(b.dataset.heroReveal),
    );
    if (reduce) {
      gsap.set(items, { opacity: 1 });
      return;
    }

    let observer: IntersectionObserver | undefined;
    const ctx = gsap.context(() => {
      items.forEach((el) => {
        gsap.fromTo(
          el,
          { opacity: 0, y: 20 },
          {
            opacity: 1,
            y: 0,
            duration: 0.4,
            ease: "power2.out",
            delay: Number(el.dataset.heroReveal) * 0.05,
            clearProps: "transform",
          },
        );
      });

      // Floating gradient inside the emphasised words.
      const accents = gsap.utils.toArray<HTMLElement>(
        scope.querySelectorAll("h1 em"),
      );
      const drift: gsap.core.Tween[] = [];
      accents.forEach((em) => {
        BLOBS.forEach((n, i) => {
          gsap.set(em, {
            [`--b${n}x`]: `${20 + i * 30}%`,
            [`--b${n}y`]: `${30 + (i % 2) * 40}%`,
          });
          drift.push(
            gsap.to(em, {
              [`--b${n}x`]: "random(-10, 110)%",
              [`--b${n}y`]: "random(-20, 120)%",
              duration: `random(2.6, 4.2)`,
              ease: "sine.inOut",
              repeat: -1,
              repeatRefresh: true,
              delay: i * 0.4,
            }),
          );
        });
      });
      observer = new IntersectionObserver(([entry]) =>
        drift.forEach((t) => (entry.isIntersecting ? t.play() : t.pause())),
      );
      observer.observe(scope);
    }, scope);

    return () => {
      observer?.disconnect();
      ctx.revert();
    };
  }, []);

  return <span ref={marker} hidden />;
}
